import {camelCase, has, toLower} from 'lodash';
import PropTypes from 'prop-types';

import chemicalIcon from '../images/icons/chemical.svg';
import clinicalAnnotationIcon from '../images/icons/clinicalAnnotation.svg';
import diseaseIcon from '../images/icons/disease.svg';
import geneIcon from '../images/icons/gene.svg';
import guidelineIcon from '../images/icons/guideline.svg';
import haplotypeIcon from '../images/icons/haplotype.svg';
import labelIcon from '../images/icons/label.svg';
import literatureIcon from '../images/icons/literature.svg';
import pathwayIcon from '../images/icons/pathway.svg';
import relationshipIcon from '../images/icons/relationship.svg';
import variantIcon from '../images/icons/variant.svg';
import variantAnnotationIcon from '../images/icons/variantAnnotation.svg';
import vipIcon from '../images/icons/vip.svg';

export const reactSelectOptionProps = PropTypes.shape({
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
});

export const formOptionsArray = PropTypes.arrayOf(reactSelectOptionProps);

export const markdownProps = PropTypes.shape({
  id: PropTypes.number.isRequired,
  html: PropTypes.string.isRequired,
});

export const crossReferenceProps = PropTypes.shape({
  id: PropTypes.number,
  resources: PropTypes.string.isRequired,
  name: PropTypes.string,
  resourceId: PropTypes.string.isRequired,
  _url: PropTypes.string.isRequired,
});

export const literatureProps = PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  journal: PropTypes.string,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  authors: PropTypes.arrayOf(PropTypes.string),
  crossReferences: PropTypes.arrayOf(crossReferenceProps),
});

export const dataAnnotationProps = PropTypes.shape({
  id: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  markdown: markdownProps,
  text: PropTypes.string,
  sources: PropTypes.arrayOf(PropTypes.string).isRequired,
  literature: PropTypes.arrayOf(literatureProps),
});

const Types = {
  chemical: {
    displayType: 'Molecule',
    icon: chemicalIcon,
    url: (id) => `/chemical/${id}`,
    overviewCounts: ['prescribingInfo', 'labelAnnotation', 'clinicalAnnotation', 'pathway'],
    searchShowAnnCounts: true,
  },
  chromosome: {
    displayType: 'Chromosome',
  },
  clinicalAnnotation: {
    displayType: 'Clinical Annotation',
    url: (id) => `/clinicalAnnotation/${id}`,
    icon: clinicalAnnotationIcon,
  },
  combination: {
    displayType: 'Combination',
    overviewCounts: ['prescribingInfo', 'labelAnnotation', 'clinicalAnnotation', 'pathway'],
    url: (id) => `/combination/${id}`,
    searchShowAnnCounts: true,
  },
  connection: {
    displayType: 'Connection',
    icon: relationshipIcon,
  },
  control: {
    displayType: 'Control',
  },
  conversion: {
    displayType: 'Conversion',
  },
  crossReference: {
    displayType: 'Cross Reference',
  },
  dataAnnotation: {
    displayType: 'Data Annotation',
    icon: literatureIcon,
    url: (id) => `/dataAnnotation/${id}`,
    propTypes: dataAnnotationProps,
  },
  disease: {
    displayType: 'Disease',
    icon: diseaseIcon,
    url: (id) => `/disease/${id}`,
    overviewCounts: ['clinicalAnnotation', 'pathway'],
  },
  dosingGuidelineAnnotation: {
    displayType: 'Clinical Guideline Annotation',
  },
  dosingGuidelineAnnotationGroup: {
    displayType: 'Clinical Guideline Annotation Group',
  },
  entity: {
    displayType: 'Entity',
  },
  gene: {
    displayType: 'Gene',
    icon: geneIcon,
    url: (id) => `/gene/${id}`,
    overviewCounts: ['prescribingInfo', 'clinicalAnnotation', 'pathway'],
    searchShowAnnCounts: true,
  },
  guidelineAnnotation: {
    displayType: 'Clinical Guideline Annotation',
    url: (id) => `/guidelineAnnotation/${id}`,
    icon: guidelineIcon,
  },
  haplotype: {
    displayType: 'Haplotype',
    icon: haplotypeIcon,
    url: (id) => `/haplotype/${id}`,
    overviewCounts: ['prescribingInfo', 'clinicalAnnotation'],
    searchShowAnnCounts: true,
  },
  haplotypeSet: {
    displayType: 'Haplotype Set',
  },
  labelAnnotation: {
    displayType: 'Drug Label Annotation',
    url: (id) => `/labelAnnotation/${id}`,
    icon: labelIcon,
  },
  literature: {
    displayType: 'Literature',
    icon: literatureIcon,
    url: (id) => `/literature/${id}`,
    propTypes: literatureProps,
    searchKeys: ['resourceId', 'title', 'journal', 'year', 'authors'],
  },
  multilinkAnnotation: {
    displayType: 'Multilink Annotation',
  },
  ontologyTerm: {
    displayType: 'Ontology Term',
  },
  pathway: {
    displayType: 'Pathway',
    icon: pathwayIcon,
    url: (id) => `/pathway/${id}`,
    overviewCounts: ['pathway', 'literature'],
    searchKeys: ['name', 'categories', 'summary.html'],
  },
  phenotype: {
    displayType: 'Disease',
    icon: diseaseIcon,
    url: (id) => `/disease/${id}`,
  },
  user: {
    displayType: 'User',
  },
  variant: {
    displayType: 'Variant',
    icon: variantIcon,
    url: (id) => `/variant/${id}`,
    overviewCounts: ['clinicalAnnotation', 'variantAnnotation'],
    searchShowAnnCounts: true,
  },
  variantAnnotation: {
    displayType: 'Variant Annotation',
    icon: variantAnnotationIcon,
    currentFormat: 2,
    url: (id) => `/variantAnnotation/${id}`,
  },
  variantDrugAnnotation: {
    displayType: 'Variant Drug Annotation',
    url: (id) => `/variantAnnotation/${id}`,
    icon: variantAnnotationIcon,
  },
  variantFunctionalAssayAnnotation: {
    displayType: 'Variant Functional Assay Annotation',
    url: (id) => `/variantAnnotation/${id}`,
    icon: variantAnnotationIcon,
  },
  variantPhenotypeAnnotation: {
    displayType: 'Variant Phenotype Annotation',
    url: (id) => `/variantAnnotation/${id}`,
    icon: variantAnnotationIcon,
  },
  vip: {
    displayType: 'VIP',
    icon: vipIcon,
  },
  vipGene: {
    displayType: 'VIP',
    icon: vipIcon,
    url: (id) => `/vip/${id}`,
  },
  submission: {
    displayType: 'Submission',
    url: (id) => `/submission/${id}`,
  },
};
export default Types;

const obsoleteTypes = {
  dosingGuideline: 'guidelineAnnotation',
  guideline: 'guidelineAnnotation',
  drug: 'chemical',
  drugLabel: 'labelAnnotation',
  label: 'labelAnnotation',
};

export function normalizeType(type) {
  if (!type) {
    return undefined;
  }
  if (Types[type]) {
    return type;
  }
  const ccType = camelCase(type);
  if (Types[ccType]) {
    return ccType;
  }
  if (obsoleteTypes[ccType]) {
    return obsoleteTypes[ccType];
  }

  const keys = Object.keys(Types);
  const lcType = toLower(type);
  for (let x = 0; x < keys.length; x += 1) {
    const t = Types[keys[x]];
    if (t.displayType && toLower(t.displayType) === lcType) {
      return keys[x];
    }
  }
  return undefined;
}

export function getBaseUrl(id, type) {
  const ccType = camelCase(type);
  const clz = Types[ccType];
  if (!clz || !has(clz, 'url')) {
    return null;
  }
  return clz.url(id);
}
